// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { Bundle } from '@/bundle/api/types';
import {
  UpdateBundleParameters,
  updateBundle,
} from '@/bundle/api/update-bundle';
import { bundleQueryKey, bundlesQueryKey } from '@/bundle/hooks/use-bundles';
import { isDuplicateBundleNameError } from '@/bundle/utilities/duplicate-bundle-name-error';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { nullifyEmptyString } from '@/utilities/nullify-empty-string';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  /**
   * Controls the visibility of the dialog.
   */
  isOpen: boolean;

  /**
   * The bundle being edited.
   */
  bundle: Bundle;

  /**
   * Callback fired when the dialog should close.
   */
  onClose: () => void;
}

/**
 * Dialog for editing a bundle's name and description.
 * Members are edited on the detail page's Items section, not here.
 */
export function EditBundleDialog(props: Props) {
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, setError, formState, watch } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        name: props.bundle.name,
        description: props.bundle.description,
      },
    });

  // Re-sync the form with the latest bundle only on the closed-to-open
  // transition; re-running on bundle identity changes while open would let
  // a background refetch wipe in-progress edits
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (props.isOpen && !wasOpenRef.current) {
      reset({
        name: props.bundle.name,
        description: props.bundle.description,
      });
    }
    wasOpenRef.current = props.isOpen;
  }, [props.isOpen, props.bundle, reset]);

  const updateBundleMutation = useMutation({
    mutationFn: (parameters: UpdateBundleParameters) =>
      updateBundle(props.bundle.key, parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bundleQueryKey(props.bundle.key),
      });
      queryClient.invalidateQueries({ queryKey: bundlesQueryKey });
      props.onClose();
    },
    onError: (error) => {
      // The duplicate-name case is a handled validation error with an inline
      // message; anything else is unexpected and must reach Sentry alongside
      // the alert (4xx responses are not auto-captured)
      if (isDuplicateBundleNameError(error)) {
        setError('name', {
          type: 'custom',
          message: 'A bundle with this name already exists',
        });
        return;
      }
      if (!handleAPIError(error).sentToSentry) {
        captureException(error);
      }
    },
  });

  const isAlertOpen =
    updateBundleMutation.isError &&
    !isDuplicateBundleNameError(updateBundleMutation.error);

  const currentDescription = watch('description');
  const normalizedDescription = nullifyEmptyString(currentDescription);
  const descriptionChanged = normalizedDescription !== props.bundle.description;

  const isDirty =
    formState.dirtyFields.name ||
    (formState.dirtyFields.description && descriptionChanged);

  const handleClose = () => {
    if (updateBundleMutation.isPending) {
      return;
    }
    props.onClose();
    updateBundleMutation.reset();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const parameters: UpdateBundleParameters = {};
    if (formState.dirtyFields.name) {
      parameters.name = data.name;
    }
    if (formState.dirtyFields.description && descriptionChanged) {
      parameters.description = normalizedDescription;
    }

    if (Object.entries(parameters).length > 0) {
      updateBundleMutation.mutate(parameters);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while updating the bundle."
        onClose={updateBundleMutation.reset}
      />
      <DialogTitle>Edit bundle</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} paddingTop={1}>
          <FormTextField name="name" label="Name" control={control} />
          <FormTextField
            name="description"
            label="Description"
            control={control}
            isMultiline
            minimumRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={handleClose}
          disabled={updateBundleMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!isDirty || updateBundleMutation.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
